import type { GaComparison } from '../types/ToolRecommenderTypes';
import { TOOL_FIELD_ORDER } from './toolFieldLabels';

export interface ComparedField {
  key: string;
  base: unknown;
  generated: unknown;
  changed: boolean;
}

const FLOAT_TOLERANCE = 1e-6;

/**
 * Fields never shown in the result views: the cutting diameter/length are the user's
 * dimensional constraints, not GA outputs (user decision 2026-09-08).
 */
export const HIDDEN_RESULT_FIELDS: readonly string[] = ['d1', 'l1'];

function isVisibleField(key: string): boolean {
  return !HIDDEN_RESULT_FIELDS.includes(key);
}

function valuesDiffer(base: unknown, generated: unknown): boolean {
  if (base === undefined || base === null || generated === undefined || generated === null) {
    return false;
  }
  if (typeof base === 'number' && typeof generated === 'number') {
    return Math.abs(base - generated) > FLOAT_TOLERANCE;
  }
  return String(base) !== String(generated);
}

function canonicalSort(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const orderA = TOOL_FIELD_ORDER.indexOf(a);
    const orderB = TOOL_FIELD_ORDER.indexOf(b);
    if (orderA === -1 && orderB === -1) return a.localeCompare(b);
    if (orderA === -1) return 1;
    if (orderB === -1) return -1;
    return orderA - orderB;
  });
}

/** Index of the comparison to show by default: the one with the highest score. */
export function defaultComparisonIndex(rows: GaComparison[]): number {
  let bestIndex = 0;
  let bestScore = -Infinity;
  rows.forEach((row, index) => {
    if (typeof row.score === 'number' && row.score > bestScore) {
      bestScore = row.score;
      bestIndex = index;
    }
  });
  return bestIndex;
}

/** One row per generated-tool field, paired with the base tool's value. */
export function comparisonFields(row: GaComparison): ComparedField[] {
  const generated = row.generatedTool ?? {};
  const base = row.baseTool ?? {};
  return canonicalSort(Object.keys(generated).filter(isVisibleField)).map((key) => {
    const baseValue = key in base && base[key] !== null ? base[key] : undefined;
    return {
      key,
      base: baseValue,
      generated: generated[key],
      changed: valuesDiffer(baseValue, generated[key]),
    };
  });
}

/** Visible generated-tool characteristics across all comparisons, in canonical order. */
export function generatedFieldKeys(rows: GaComparison[]): string[] {
  const keys = new Set<string>();
  rows.forEach((row) =>
    Object.keys(row.generatedTool ?? {})
      .filter(isVisibleField)
      .forEach((key) => keys.add(key))
  );
  return canonicalSort([...keys]);
}

/** Base-tool fields the GA does not optimise — shown in the Advanced section. */
export function advancedFields(row: GaComparison): { key: string; value: unknown }[] {
  const base = row.baseTool ?? {};
  const generatedKeys = new Set(Object.keys(row.generatedTool ?? {}));
  return canonicalSort(
    Object.keys(base).filter((key) => isVisibleField(key) && !generatedKeys.has(key))
  ).map((key) => ({ key, value: base[key] }));
}

/** Generated minus base score, or null when either is missing. */
export function scoreDelta(row: GaComparison): number | null {
  if (typeof row.score !== 'number' || typeof row.baseScore !== 'number') return null;
  return row.score - row.baseScore;
}

export function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return String(value);
    return String(Number(value.toFixed(3)));
  }
  return String(value);
}
